import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Check, Loader2, XCircle, AlertTriangle, Zap } from "lucide-react";

/**
 * /billing/success?session_id=cs_…
 * Polls the backend until the payment is confirmed and tokens credited.
 * MANDATORY per Stripe playbook — webhook may lag, polling is the safety net.
 */
export default function BillingSuccess() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = search.get("session_id");

  const [tx, setTx] = useState(null);
  const [status, setStatus] = useState("polling"); // polling | success | expired | error
  const [errorMsg, setErrorMsg] = useState("");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      setErrorMsg("Missing session_id");
      return;
    }
    let active = true;
    let attempt = 0;
    const MAX = 12;        // ~36s total
    const INTERVAL = 3000;

    const poll = async () => {
      attempt += 1;
      if (!active) return;
      setAttempts(attempt);
      try {
        const { data } = await api.get(`/billing/session/${sessionId}`);
        if (!active) return;
        setTx(data);
        if (data.credited || data.payment_status === "paid") {
          setStatus("success");
          window.dispatchEvent(new Event("wallet:refresh"));
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempt >= MAX) {
          setStatus("error");
          setErrorMsg("Payment is taking longer than usual to confirm. Check back in a minute — your wallet will update automatically.");
          return;
        }
        setTimeout(poll, INTERVAL);
      } catch (e) {
        if (!active) return;
        const msg = formatApiError(e.response?.data?.detail) || e.message;
        if (attempt >= MAX) {
          setStatus("error");
          setErrorMsg(msg);
        } else {
          setTimeout(poll, INTERVAL);
        }
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background grid place-items-center px-6">
      <div className="max-w-md w-full border border-border bg-card rounded-sm p-8 text-center" data-testid="billing-success-card">
        {status === "polling" && (
          <>
            <Loader2 className="h-10 w-10 mx-auto animate-spin text-primary" />
            <h1 className="font-display font-black tracking-tight text-2xl mt-4">Confirming your payment</h1>
            <p className="text-sm text-muted-foreground mt-2 font-mono">attempt {attempts} / 12 · checking with Stripe…</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="h-14 w-14 mx-auto bg-emerald-500/15 rounded-full grid place-items-center">
              <Check className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h1 className="font-display font-black tracking-tight text-2xl mt-4" data-testid="success-heading">Payment successful</h1>
            <div className="mt-4 flex items-center justify-center gap-2 font-mono">
              <Zap className="h-4 w-4 text-primary" />
              <span className="font-bold text-xl tabular-nums">+{Number(tx?.tokens || 0).toLocaleString()}</span>
              <span className="text-sm text-muted-foreground">tokens credited</span>
            </div>
            <p className="text-xs font-mono text-muted-foreground mt-1">{tx?.package_name} · ${Number(tx?.amount_usd || 0).toFixed(2)} paid</p>
            <div className="mt-8 grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => navigate("/billing")} className="rounded-sm" data-testid="success-back-billing">View wallet</Button>
              <Button onClick={() => navigate("/workspace")} className="rounded-sm" data-testid="success-workspace">Start chatting</Button>
            </div>
          </>
        )}
        {status === "expired" && (
          <>
            <XCircle className="h-12 w-12 mx-auto text-muted-foreground" />
            <h1 className="font-display font-black tracking-tight text-2xl mt-4">Session expired</h1>
            <p className="text-sm text-muted-foreground mt-2">Your checkout session has expired without a payment. No charge was made.</p>
            <Button onClick={() => navigate("/billing")} className="rounded-sm mt-6 w-full">Try again</Button>
          </>
        )}
        {status === "error" && (
          <>
            <AlertTriangle className="h-12 w-12 mx-auto text-amber-500" />
            <h1 className="font-display font-black tracking-tight text-2xl mt-4">We're still checking</h1>
            <p className="text-sm text-muted-foreground mt-2">{errorMsg}</p>
            <Button onClick={() => navigate("/billing")} className="rounded-sm mt-6 w-full">Back to billing</Button>
          </>
        )}
      </div>
    </div>
  );
}
